import React, { useState, useEffect, useRef } from "react";
import MDEditor from "@uiw/react-md-editor";
import mermaid from "mermaid";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Tooltip, Menu, MenuItem, Dialog, DialogTitle, DialogActions, Button } from "@mui/material";
import * as XLSX from "xlsx";

const randomid = () => parseInt(String(Math.random() * 1e15), 10).toString(36);

const Code = ({ children = [], className }) => {
  const demoid = useRef(`dome${randomid()}`);
  const code = getCode(children);
  const demo = useRef(null);

  useEffect(() => {
    mermaid.initialize({ startOnLoad: false });

    if (demo.current) {
      mermaid
        .render(demoid.current, code)
        .then(({ svg }) => {
          if (demo.current) {
            demo.current.innerHTML = svg;
          }
        })
        .catch((error) => {
          if (demo.current) {
            demo.current.innerHTML = error.toString();
          }
        });
    }
  }, [code]);

  if (typeof code === "string" && typeof className === "string" && /^language-mermaid/.test(className.toLowerCase())) {
    return <div ref={demo} />;
  }
  return <code className={String(className)}>{children}</code>;
};

const getCode = (arr = []) => {
  if (!Array.isArray(arr)) {
    return String(arr);
  }
  return arr
    .map((dt) => {
      if (typeof dt === "string") {
        return dt;
      }
      if (dt.props && dt.props.children) {
        return getCode(dt.props.children);
      }
      return false;
    })
    .filter(Boolean)
    .join("");
};

const exportToExcel = (headerCells, tableRows, tableIndex) => {
  const data = tableRows
    .map((row) => {
      const cells = row.split("|").filter((cell) => cell.trim().length > 0);
      return cells.map((cell) => cell.trim());
    })
    .filter((row) => row.some((cell) => cell !== "-")); // Filter out rows that contain only dashes

  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, `Table_${tableIndex + 1}`);

  XLSX.writeFile(workbook, `table_${tableIndex + 1}.xlsx`);
};

const exportAllTablesToExcel = (tables, onSameSheet) => {
  const workbook = XLSX.utils.book_new();

  tables.forEach((tableRows, tableIndex) => {
    const data = tableRows
      .map((row) => {
        const cells = row.split("|").filter((cell) => cell.trim().length > 0);
        return cells.map((cell) => cell.trim());
      })
      .filter((row) => row.some((cell) => cell !== "-")); // Filter out rows that contain only dashes

    const worksheet = XLSX.utils.aoa_to_sheet(data);

    if (onSameSheet) {
      if (tableIndex === 0) {
        XLSX.utils.book_append_sheet(workbook, worksheet, "All Tables");
      } else {
        XLSX.utils.sheet_add_aoa(
          workbook.Sheets["All Tables"],
          [[""], ...data],
          { origin: -1 } // Append new data after the last row
        );
      }
    } else {
      XLSX.utils.book_append_sheet(workbook, worksheet, `Table_${tableIndex + 1}`);
    }
  });

  XLSX.writeFile(workbook, `all_tables.xlsx`);
};

const MermaidEditor = ({ rawResponse }) => {
  const [value, setValue] = useState(rawResponse);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    setValue(rawResponse);
  }, [rawResponse]);

  const getTables = (response) => {
    const lines = response.split("\n");
    let tables = [];
    let currentTable = [];

    lines.forEach((line) => {
      if (line.includes("|")) {
        currentTable.push(line);
      } else if (currentTable.length > 0) {
        tables.push(currentTable);
        currentTable = [];
      }
    });

    if (currentTable.length > 0) {
      tables.push(currentTable);
    }

    return tables;
  };

  const handleDownloadClick = (event) => {
    if (tables.length === 1) {
      handleMenuItemClick(0);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuItemClick = (tableIndex) => {
    const tableRows = tables[tableIndex];
    const headerIndex = tableRows.findIndex((row) => /^[-| ]+$/.test(row)) - 1;
    const headerCells =
      tableRows[headerIndex]
        ?.split("|")
        .filter((cell) => cell.trim().length > 0)
        .map((cell) => cell.trim()) || [];
    exportToExcel(headerCells, tableRows, tableIndex);
    setAnchorEl(null);
  };

  const handleDownloadAllClick = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = (onSameSheet) => {
    exportAllTablesToExcel(tables, onSameSheet);
    setDialogOpen(false);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const tables = getTables(rawResponse);

  if (!rawResponse) {
    return null;
  }

  return (
    <div>
      <MDEditor.Markdown
        source={value}
        className="mermaid-editor"
        components={{
          code: Code,
        }}
      />
      {tables.length > 0 && (
        <div className="table_responce_action">
          <div className="table_responce_action_flex">
            <Tooltip className="blue-tooltip" title="Download Excel Sheet" placement="bottom" arrow>
              <button type="button" className="btn btn-default" style={{ padding: "5px 10px", fontSize: "12px" }} onClick={handleDownloadClick}>
                <FileDownloadOutlinedIcon fontSize="small" /> Download Excel
              </button>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              {tables.length > 1 && <MenuItem onClick={handleDownloadAllClick}>Download All Tables</MenuItem>}
              {tables.map((_, index) => (
                <MenuItem key={index} onClick={() => handleMenuItemClick(index)}>
                  Download Table {index + 1}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Download All Tables</DialogTitle>
        <DialogActions>
          <Button onClick={() => handleDialogClose(true)} color="primary">
            On same sheet
          </Button>
          <Button onClick={() => handleDialogClose(false)} color="primary" autoFocus>
            On different sheets
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MermaidEditor;
